"use client";

import {
  motion,
  type MotionStyle,
  type MotionValue,
  useScroll,
  useTransform,
} from "motion/react";
import { useRef } from "react";

import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";

// =============================================================================

type WordProps = {
  word: string;
  progress: MotionValue<number>;
  range: [number, number];
};

function Word({ word, progress, range }: WordProps) {
  // 0 → 1 while the word lights up: the reveal-word utility turns it into
  // an opacity, dimmed per theme (globals.css)
  const reveal = useTransform(progress, range, [0, 1]);
  return (
    <motion.span
      style={{ "--reveal": reveal } as MotionStyle}
      className="inline-block reveal-word"
    >
      {word}
    </motion.span>
  );
}

// =============================================================================

type ScrollTextRevealProps = {
  text: string;
  /** Text tag (not ElementType: R3F adds three.js elements to the JSX ones) */
  as?: "p" | "h2" | "h3" | "h4" | "div";
  className?: string;
  /** Scroll offsets of the reveal when it tracks its own element */
  offset?: [string, string];
  /**
   * External progress (e.g. a pinned section): the words are revealed while
   * it goes through `range`
   */
  progress?: MotionValue<number>;
  range?: [number, number];
};

/**
 * Words light up one after another as the text scrolls through the viewport
 * (or along an external progress).
 */
export function ScrollTextReveal({
  text,
  as: Tag = "p",
  className,
  offset = ["start 0.85", "end 0.45"],
  progress,
  range = [0, 1],
}: ScrollTextRevealProps) {
  // Fits every allowed tag
  const ref = useRef<
    HTMLParagraphElement & HTMLHeadingElement & HTMLDivElement
  >(null);
  const shouldReduceMotion = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    // @ts-expect-error -- motion types offsets as template literals
    offset,
  });

  const source = progress ?? scrollYProgress;
  const [from, to] = progress ? range : [0, 1];
  const words = text.split(" ");
  const step = (to - from) / words.length;

  return (
    <Tag ref={ref} className={cn("flex flex-wrap", className)}>
      {words.map((word, index) => {
        const start = from + index * step;
        return (
          <span key={`${word}-${index}`} className="mr-[0.25em] last:mr-0">
            {shouldReduceMotion ? (
              word
            ) : (
              <Word
                word={word}
                progress={source}
                range={[start, start + step]}
              />
            )}
          </span>
        );
      })}
    </Tag>
  );
}

// =============================================================================
